'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/navigation';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { GRACE_DAYS } from '@/lib/plans';
import { siteLinks } from '@/lib/site-links';

/**
 * "İnce yazı" — /pricing sayfasının kapanışı.
 *
 * Üç not fiyat sayfasının üç klasik tuzağını kapatıyor: kredi neye gidiyor,
 * limiti aşınca ne oluyor, koltuk başına ücret var mı. Üçü de satın almadan
 * ÖNCE sorulan ve genellikle yalnızca sözleşmede cevabı olan sorular; sayfanın
 * duruşu bunları açıkça yazmak.
 *
 * ── Metinler yeni değil ───────────────────────────────────────────────────
 *
 * Anasayfanın fiyat bölümünde üç not şeridi vardı ve 10 Ağu 2026'da kullanıcı
 * isteğiyle oradan kaldırıldı; çevirileri `pricing.notes.*` altında DURUYORDU
 * (bkz. components/pricing.tsx sonundaki yorum: "tam karşılaştırma sayfası
 * açıldığında oraya taşınmaya hazır bekliyorlar"). Bu bölüm o taşımadır —
 * anahtarlar yeniden yazılmadı, yeri değişti.
 *
 * ── Rakam ─────────────────────────────────────────────────────────────────
 *
 * Ek süre `GRACE_DAYS`ten geliyor. Çeviri dosyasında yalnızca `{days}` yer
 * tutucusu var: yumuşak sınırın kaç gün olduğu ürün kararı, metin kararı
 * değil (packages/shared/plans.ts).
 *
 * ── Zemin ─────────────────────────────────────────────────────────────────
 *
 * --gray-50. Sayfanın nefesi: başlık (beyaz) → planlar (gri) → planın içi
 * (beyaz) → ince yazı (gri) → CTA (beyaz zemin + koyu şerit). İki aynı renk
 * yan yana gelmiyor; bu bölüm beyaz olsaydı CTA ile tek gövde gibi akardı.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Sitenin alt bölümlerinin tamamı gibi giriş hareketi YOK (`initial={false}`,
 * kullanıcı kararı 9 Eyl 2026). Variant'lar duruyor; sıra hat → başlık →
 * gövde. Döngü ve parallax yok.
 */

/** Sıra: en çok yanlış anlaşılandan en incesine. Anahtarlar `pricing.notes`. */
const NOTES = ['credits', 'limit', 'seats'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
};

const textV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function PricingNotes() {
  const t = useTranslations('pricing');
  const uid = useId();

  return (
    <section className="mind" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="mind-inner" variants={sectionV} initial={false} animate="show">
            <motion.div className="mind-head" variants={headV}>
              <p className="mind-eyebrow">{t('fine.eyebrow')}</p>
              <h2 className="mind-title" id={`${uid}-title`}>
                {t.rich('fine.title', { hl })}
              </h2>
            </motion.div>

            <motion.ul className="mind-list" variants={listV}>
              {NOTES.map((key) => (
                <motion.li className="mind-cell" key={key} variants={cellV}>
                  <motion.span className="mind-rule" aria-hidden="true" variants={ruleV} />

                  <motion.h3 className="mind-name" variants={textV}>
                    {t(`notes.${key}.title`)}
                  </motion.h3>
                  <motion.p className="mind-text" variants={textV}>
                    {t(`notes.${key}.body`, { days: GRACE_DAYS })}
                  </motion.p>
                </motion.li>
              ))}
            </motion.ul>

            {/* Mesafeli satışta ön bilgilendirme SATIN ALMADAN ÖNCE
                erişilebilir olmak zorunda; ödeme ekranındaki onay kutusu tek
                başına yetmez, çünkü o noktada kişi zaten kart bilgisini
                girmiş oluyor. Fiyat sayfasının kapanışı bu yüzden iki
                belgeye — ve 14 Eki 2026'dan beri teslimat/iade özetine —
                açılıyor. */}
            <motion.p className="mind-legal" variants={textV}>
              {t.rich('fine.legal', {
                preInformation: (chunks) => (
                  <Link href={siteLinks.preInformation.href}>{chunks}</Link>
                ),
                distanceSales: (chunks) => (
                  <Link href={siteLinks.distanceSales.href}>{chunks}</Link>
                ),
              })}
              {/* Ayrı anahtar, `fine.legal`in içinde değil: o cümleye yeni bir
                  etiket eklemek 11 dilin mevcut çevirisini argüman farkıyla
                  bozuk sayardı (check.mjs). Bu parça çevrilene kadar
                  İngilizceye düşüyor. */}
              {' · '}
              <Link href={siteLinks.deliveryReturns.href}>{t('fine.deliveryReturns')}</Link>
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
